import { DataProvider } from 'react-admin';
import { CommandParameterDraft } from '../../types/commands';

export interface SaveDraftParametersResult {
  /** Names of the parameters that reached the API, in the order they were sent. */
  saved: string[];
  /** Names of the parameters the API refused, in the order they were attempted. */
  failed: string[];
}

/**
 * Sort by the order the user gave, then renumber 1..n so the list the user sees
 * is exactly the order the parameters are POSTed in after the command is created.
 */
export const normalizeDraftOrder = (parameters: CommandParameterDraft[]): CommandParameterDraft[] =>
  [...parameters].sort((a, b) => a.order - b.order).map((parameter, index) => ({ ...parameter, order: index + 1 }));

/**
 * Writes the drafted parameters of a freshly created command to
 * `POST /v3/api/commands/{command}/parameters`, one at a time and in display
 * order (there is no nested-create on `POST /v3/api/commands`).
 *
 * A failing parameter never rolls back the command - it already exists - so the
 * caller gets the exact split between what was stored and what was not, and can
 * report that instead of a blanket success.
 */
export const saveDraftParameters = async (
  dataProvider: DataProvider,
  commandId: number | string,
  drafts: CommandParameterDraft[],
): Promise<SaveDraftParametersResult> => {
  const saved: string[] = [];
  const failed: string[] = [];

  for (const draft of normalizeDraftOrder(drafts)) {
    const { draft_id: _draftId, ...values } = draft;

    try {
      await dataProvider.create(`commands/${commandId}/parameters`, {
        data: { ...values, command_id: commandId },
      });
      saved.push(draft.name);
    } catch (error) {
      failed.push(draft.name);
    }
  }

  return { saved, failed };
};
